import { createTheme } from '@mui/material/styles';

import {
  DEGRADE_SURVOL, MARQUE_DEBUT, MARQUE_FIN, PADDING_CELLULE_Y,
  PADDING_CELLULE_Y_MOBILE, PILE_POLICES, RAYON_COMMANDE, RAYON_SURFACE,
  degrade,
} from './jetons';

/**
 * Le thème, rendu par une FONCTION du mode — clair ou sombre.
 *
 * 🔴 POURQUOI UNE FONCTION ET NON DEUX THÈMES. Deux `createTheme` complets
 * auraient dupliqué tout ce qui ne dépend PAS de la couleur : la typographie,
 * la densité, les rayons, les huit overrides de composants. Une valeur
 * dupliquée est une valeur qui diverge — c'est le défaut que ce lot vient de
 * corriger sur le dégradé, recopié dans sept endroits. Ici **une seule
 * définition**, et seule la palette est choisie selon le mode.
 *
 * ⚠️ LE MODE SOMBRE EST POSÉ MAINTENANT, PAS APRÈS, et ce n'est pas de
 * l'anticipation gratuite : le poser après aurait voulu dire relire les 40
 * écrans pour y chercher les couleurs en dur qu'on aurait laissées s'installer
 * entre-temps. Le rendre possible coûte une palette ; le rajouter coûte un
 * audit.
 */

/** Les couleurs qui dépendent du mode, et elles seules. */
const PALETTES = {
  light: {
    primary: { main: MARQUE_DEBUT, light: '#8b9ef0', dark: '#4c63d2' },
    secondary: { main: MARQUE_FIN, light: '#9a6bb8', dark: '#5a3a7a' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    // 🔴 `#64748b` NE TENAIT PAS LE SEUIL, ET PAS SEULEMENT SUR UNE LIGNE
    // TEINTÉE. Mesuré : 4,76 sur le blanc du papier, **4,55 sur le fond de
    // l'application** — pour un seuil AA de 4,5. Il frôlait partout, et
    // n'importe quelle surface un peu colorée le faisait passer dessous (4,26
    // sur une ligne à stock bas, 3,49 sur une ligne épuisée). Le code-barres du
    // tableau des produits est un `caption` de 12 px : c'est exactement le
    // texte que ce seuil protège.
    // `#475569` tient les quatre surfaces avec de la marge : 7,58 / 7,24 /
    // 6,58 / 5,22. ⚠️ Même classe de défaut que les titres du lot RU1 — un
    // texte lisible « de justesse » n'est pas lisible, il attend une surface.
    text: { primary: '#1e293b', secondary: '#475569' },
    divider: '#e2e8f0',
  },
  dark: {
    // ⚠️ LES COULEURS DE MARQUE SONT ÉCLAIRCIES EN MODE SOMBRE, pas reprises
    // telles quelles : `#667eea` sur un fond `#0f172a` tombe sous le rapport de
    // contraste 4,5:1 exigé pour du texte. Une palette sombre n'est pas une
    // palette claire inversée.
    primary: { main: '#8b9ef0', light: '#aab8f5', dark: MARQUE_DEBUT },
    secondary: { main: '#a97fd0', light: '#c3a3e0', dark: MARQUE_FIN },
    // ⚠️ GRIS NEUTRE, PAS BLEU ARDOISE. Les valeurs précédentes — `#0f172a` et
    // `#1e293b` — sont des bleus : l'interface entière était donc teintée, et
    // le violet d'accent s'y fondait au lieu de s'en détacher. **C'est la
    // décision de la refonte** : l'accent doit être le SEUL endroit coloré.
    // Un gris strictement neutre (`#1e1e1e`) a été écarté comme un peu mort ;
    // celui-ci garde une pointe de chaleur qui s'accorde au violet.
    background: { default: '#121418', paper: '#1c1f26' },
    text: { primary: '#e8eaed', secondary: '#9aa0a6' },
    divider: '#2f333b',
  },
};

/** L'ombre portée, qui ne peut pas être la même sur fond clair et sur fond sombre. */
const OMBRES = {
  // Sur fond clair, une ombre est du noir transparent.
  light: {
    carte: '0 4px 20px rgba(0,0,0,0.08)',
    carteSurvol: '0 8px 30px rgba(0,0,0,0.12)',
    papier: '0 2px 12px rgba(0,0,0,0.06)',
    bouton: '0 2px 4px rgba(0,0,0,0.1)',
    boutonSurvol: '0 4px 8px rgba(0,0,0,0.15)',
    bordureCarte: '1px solid rgba(0,0,0,0.04)',
  },
  // ⚠️ Sur fond sombre, une ombre noire ne se VOIT pas. Ce qui sépare deux
  // surfaces sombres est une bordure claire, pas une ombre — d'où des ombres
  // plus marquées ET une bordure visible.
  dark: {
    carte: '0 4px 20px rgba(0,0,0,0.5)',
    carteSurvol: '0 8px 30px rgba(0,0,0,0.65)',
    papier: '0 2px 12px rgba(0,0,0,0.4)',
    bouton: '0 2px 4px rgba(0,0,0,0.4)',
    boutonSurvol: '0 4px 8px rgba(0,0,0,0.55)',
    bordureCarte: '1px solid rgba(255,255,255,0.08)',
  },
};

/**
 * Les composants qui portent DIRECTEMENT du texte saisi par l'utilisateur.
 *
 * ⚠️ La liste est celle des porteurs de NŒUD TEXTE, pas celle des conteneurs —
 * mesuré : `unicode-bidi: plaintext` posé sur une `<td>` ou sur la `<table>` ne
 * corrige rien, posé sur le `<Typography>` qui porte le texte, il corrige.
 * ⚠️ **`input` en est ABSENT, délibérément** : un champ vide n'a pas de contenu
 * sur quoi décider de sa direction.
 */
const SELECTEUR_TEXTE_LIBRE = '.MuiTypography-root, .MuiTableCell-root, '
  + '.MuiListItemText-primary, .MuiListItemText-secondary';

/**
 * @param {'light'|'dark'} mode
 * @param {'ltr'|'rtl'} direction — vient de la LANGUE, jamais d'un réglage.
 *
 * 🔴 POURQUOI LA DIRECTION EST DANS LE THÈME ET PAS SEULEMENT SUR `<html>`.
 * MUI lit `theme.direction` pour les composants dont le retournement n'est pas
 * une affaire de CSS mais de **logique** : `Drawer anchor="left"`, les flèches
 * de pagination, le sens d'ouverture des menus, `Slider`, `Tabs`. **Aucun
 * plugin de feuille de style ne peut les retourner** — ils décident en
 * JavaScript, avant d'émettre le moindre style.
 *
 * ⚠️ IL FAUT DONC LES DEUX, ET ILS NE FONT PAS LE MÊME TRAVAIL :
 *   · `theme.direction` → ce que **MUI décide** ;
 *   · `stylis-plugin-rtl` → ce que **le CSS émis** dit (`margin-left` → `margin-right`) ;
 *   · `<html dir>` → ce que **le navigateur** fait du texte brut.
 * *Trois couches, trois responsabilités — et aucune ne couvre les deux autres.*
 */
export function creerTheme(mode = 'light', direction = 'ltr') {
  const palette = PALETTES[mode] || PALETTES.light;
  const ombre = OMBRES[mode] || OMBRES.light;

  return createTheme({
    direction,
    palette: { mode, ...palette },

    // ⚠️ 8, ENTIER — le défaut de MUI. L'ancien thème posait `spacing: 7.2`,
    // d'où des demi-pixels dans toute l'application (`10.8px`, `24.3px`,
    // `1.0125rem`). Une valeur non entière n'a pas été choisie : elle a été
    // calculée à partir d'autre chose.
    spacing: 8,

    // ⚠️ AUCUN MULTIPLICATEUR. Les tailles sont celles de MUI ; seules les
    // graisses sont posées, parce que ce sont elles qui donnent le caractère
    // d'une interface d'outil — pas une réduction générale du corps.
    typography: {
      fontFamily: PILE_POLICES,
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
    },

    shape: { borderRadius: RAYON_COMMANDE },

    components: {
      // ⚠️ Ce qui vivait dans `index.css` sous forme de règles globales — fond
      // du corps, lissage, barres de défilement, anneau de focus — vit ici :
      // ces valeurs dépendent du MODE, et une feuille statique ne peut pas en
      // changer.
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: palette.background.default,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          // ⚠️ CONSERVÉ D'`index.css` SANS L'AVOIR VÉRIFIÉ AU NAVIGATEUR : rien
          // dans le code ne dit quel écran débordait, et le retirer pourrait
          // faire apparaître une barre horizontale quelque part. Ce lot ne doit
          // rien changer au contenu — on garde, et on le signale.
          'html, body': { overflowX: 'hidden' },
          // ⚠️ TIRÉES DE LA PALETTE, pas écrites en dur. La première version de
          // ce bloc portait `#1e293b` / `#475569` / `#64748b` — les anciennes
          // valeurs ardoise. Elles ont survécu au changement de palette sans
          // que rien ne le signale : une barre de défilement bleutée dans une
          // interface devenue grise. C'est le défaut de ce lot en miniature.
          '::-webkit-scrollbar': { width: 6, height: 6 },
          '::-webkit-scrollbar-track': {
            background: palette.background.paper,
            borderRadius: 3,
          },
          '::-webkit-scrollbar-thumb': {
            background: palette.divider,
            borderRadius: 3,
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: palette.text.secondary,
          },
          // L'anneau de focus prend la couleur primaire DU MODE : en dur, il
          // devenait illisible sur fond sombre.
          '.MuiButton-root:focus-visible, .MuiIconButton-root:focus-visible': {
            outline: `2px solid ${palette.primary.main}`,
            outlineOffset: 2,
          },

          /**
           * 🔴 CHAQUE TEXTE DÉCIDE DE SA PROPRE DIRECTION (RU8-c).
           *
           * Sous un paragraphe hébreu, **toute chaîne latine qui commence ou
           * finit par un chiffre ou une ponctuation** voyait ce morceau passer
           * à l'autre bout. Huit occurrences mesurées au navigateur :
           *   `789 Boulevard de l'Info, 13001 Marseille`
           *        → `Boulevard de l'Info, 13001 Marseille 789`
           *   `Écran Samsung 24"`  → `"Écran Samsung 24`
           *   `23.08.2026, 17:22 · TechSupply Pro`
           *        → `TechSupply Pro · 17:22, 23.08.2026`
           * 🔴 **L'adresse fournisseur est le vrai sujet : elle part sur les
           * bons de commande.** *Un numéro de rue déplacé est une adresse
           * FAUSSE, pas une adresse mal présentée.*
           *
           * ⚠️ `plaintext` NE FORCE RIEN — et c'est pourquoi il est ici plutôt
           * qu'une liste de champs. Il résout la direction de chaque texte sur
           * son **premier caractère fort** : le latin devient gauche-droite,
           * l'hébreu reste droite-gauche. **Mesuré des deux côtés** — `עברית`
           * rend exactement pareil avec et sans la règle. *C'est la sémantique
           * d'un texte saisi par l'utilisateur, dont on ne connaît pas la
           * langue.* Une énumération de champs, elle, vieillirait.
           *
           * 🔴 ET LA RÈGLE PORTE SUR L'ÉLÉMENT QUI CONTIENT LE TEXTE, JAMAIS
           * SUR UN CONTENEUR. Mesuré sur la page : `plaintext` posé sur la
           * `<td>` ne corrige rien, sur la `<table>` non plus ; posé sur le
           * `<Typography>` qui porte le nœud texte, il corrige. **Troisième
           * occurrence de la même loi ce chantier** — *l'isolation bidi est une
           * propriété de l'élément qui CONTIENT le texte, pas d'un ancêtre.*
           *
           * ⚠️ LES CHAMPS DE SAISIE SONT HORS DE LA RÈGLE, ET C'EST DÉLIBÉRÉ.
           * Un `<input>` VIDE n'a aucun contenu sur quoi décider : `plaintext`
           * le laisserait choisir dans le vide, et le curseur d'un utilisateur
           * hébréophone partirait du mauvais côté — il taperait à l'envers.
           * Mesuré après application : les trois champs du Login restent
           * `direction: rtl`, `unicode-bidi: normal`.
           */
          [SELECTEUR_TEXTE_LIBRE]: { unicodeBidi: 'plaintext' },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: ombre.bouton,
            '&:hover': { boxShadow: ombre.boutonSurvol, transform: 'translateY(-1px)' },
            transition: 'all 0.2s ease-in-out',
            // ⚠️ AUCUN `padding` ICI. L'ancien thème posait `'7.2px 21.6px'`,
            // censé valoir 90 % de `8px 24px` — mais le défaut réel de MUI est
            // `6px 16px`. Le bouton finissait donc **plus grand** que le défaut
            // (30,4 px contre 28), à rebours de l'intention affichée. Mesuré.
          },
          contained: {
            background: degrade(),
            '&:hover': { background: DEGRADE_SURVOL },
          },
        },
      },

      // Les transitions étaient déclarées TROIS fois : deux dans le thème et
      // quatre lignes dans `index.css`. Une seule déclaration désormais.
      MuiDrawer: {
        styleOverrides: {
          paper: { transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important' },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), '
              + 'margin 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
            // 🔴 LE VOILE D'ÉLÉVATION DE MUI, RETIRÉ — ET DU SEUL APPBAR.
            // En mode sombre, MUI pose sur toute surface `Paper` élevée un
            // `backgroundImage: linear-gradient(…)` dont **les deux arrêts sont
            // identiques** : un dégradé qui n'en est pas un, juste un voile
            // blanc destiné à simuler l'élévation. Mesuré sur l'AppBar : 9 %.
            //
            // ⚠️ IL NE FAUT SURTOUT PAS LE RETIRER GLOBALEMENT. Vérifié dans
            // `Paper.js` : la règle vise TOUT `variant="elevation"` en mode
            // sombre — Card (élévation 1, voile 5 %), Menu et Popover (8 →
            // 12 %), Dialog (24 → 16 %), Drawer temporaire. Un
            // `MuiPaper: { backgroundImage: 'none' }` aplatirait donc toutes
            // les surfaces pour corriger un bandeau.
            backgroundImage: 'none',
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: RAYON_SURFACE,
            boxShadow: ombre.carte,
            border: ombre.bordureCarte,
            '&:hover': { boxShadow: ombre.carteSurvol, transform: 'translateY(-2px)' },
            transition: 'all 0.3s ease-in-out',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: RAYON_SURFACE, boxShadow: ombre.papier },
        },
      },

      // ⚠️ Ni `height` ni `fontSize` : les défauts MUI (32 px, 0.8125rem) sont
      // conservés. C'est la puce qui fixait la hauteur d'une rangée, et la
      // rapetisser à 24,3 px ne gagnait AUCUNE ligne — mesuré — tout en passant
      // sous le confort tactile.
      MuiChip: {
        styleOverrides: { root: { borderRadius: RAYON_COMMANDE, fontWeight: 500 } },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 600,
              backgroundColor: palette.background.default,
              borderBottom: `2px solid ${palette.divider}`,
            },
          },
        },
      },

      // 🔴 LE SEUL ENDROIT OÙ LA DENSITÉ SE JOUE. Cf. `PADDING_CELLULE_Y`.
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            paddingTop: PADDING_CELLULE_Y,
            paddingBottom: PADDING_CELLULE_Y,
            // La contrepartie tactile, par les points de rupture du THÈME —
            // plus par des `!important` dans une feuille statique.
            [theme.breakpoints.down('md')]: {
              paddingTop: PADDING_CELLULE_Y_MOBILE,
              paddingBottom: PADDING_CELLULE_Y_MOBILE,
            },
          }),
        },
      },

      // 🔴 LES ACTIONS SONT GRISES AU REPOS, COLORÉES AU SURVOL — et la règle
      // vit ICI plutôt que dans les cinq écrans qui portent des tableaux.
      //
      // ⚠️ CE QU'ELLE CORRIGE : une ligne de tableau portait jusqu'à CINQ
      // icônes, chacune de sa couleur (bleu, vert, orange, rouge…). Sur trente
      // lignes, cela fait cent cinquante taches colorées, et le regard n'a plus
      // aucun endroit où se poser. La couleur d'une action est une information
      // au moment où on la vise — pas en permanence.
      //
      // ⚠️ Le ciblage passe par `.MuiTableCell-root` : hors des tableaux, les
      // boutons d'icône gardent leur couleur (la cloche du header, le sélecteur
      // de langue). Une règle globale les aurait éteints aussi.
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            '.MuiTableCell-root &': {
              color: theme.palette.text.secondary,
              transition: 'color 0.15s ease-in-out',
              // La couleur revient au survol ET au focus clavier : sans le
              // second, l'action serait invisible pour qui navigue au clavier.
              '&:hover, &:focus-visible': { color: theme.palette.primary.main },
              '&.Mui-disabled': { color: theme.palette.action.disabled },
            },
          }),
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: RAYON_COMMANDE,
              '&:hover fieldset': { borderColor: palette.primary.main },
              '&.Mui-focused fieldset': { borderColor: palette.primary.main },
            },
          },
        },
      },
    },
  });
}

export default creerTheme;
